import React, { useState } from 'react';

interface Exercise {
  name: string;
  reps: number | string;
  instructions?: string;
}

interface AMRAPTimerDisplayProps {
  totalDuration: number; // Total workout duration in seconds
  exercises: Exercise[];
  elapsedTime: number; // Current elapsed time in seconds
  onRoundComplete?: (round: number) => void;
}

export const AMRAPTimerDisplay: React.FC<AMRAPTimerDisplayProps> = ({ 
  totalDuration, 
  exercises, 
  elapsedTime,
  onRoundComplete 
}) => {
  const [roundsCompleted, setRoundsCompleted] = useState(0);
  const [currentExerciseInRound, setCurrentExerciseInRound] = useState(0);
  
  const remainingTime = Math.max(0, totalDuration - elapsedTime);
  const currentExercise = exercises[currentExerciseInRound];

  const handleExerciseComplete = () => {
    if (currentExerciseInRound < exercises.length - 1) {
      // Move to next exercise
      setCurrentExerciseInRound(prev => prev + 1);
    } else {
      // Round complete!
      const newRoundCount = roundsCompleted + 1;
      setRoundsCompleted(newRoundCount);
      setCurrentExerciseInRound(0);
      
      // Play audio cue
      const audio = new Audio('/sounds/round-complete.mp3');
      audio.play().catch(e => console.log('Audio play failed:', e));
      
      if (onRoundComplete) {
        onRoundComplete(newRoundCount);
      }
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex flex-col items-center space-y-6 py-6">
      {/* Count-up Timer */}
      <div className="text-center">
        <div className="text-6xl font-bold text-green-500">
          {formatTime(elapsedTime)}
        </div>
        <div className="text-sm text-gray-400 mt-2">
          {formatTime(remainingTime)} remaining
        </div>
      </div>
      
      {/* Rounds Completed - BIG */}
      <div className="text-center">
        <div className="text-8xl font-bold text-white">{roundsCompleted}</div>
        <div className="text-sm text-gray-400 mt-2 tracking-wide">ROUNDS COMPLETED</div>
      </div>
      
      {/* Current Exercise - Highlighted */}
      <div className="w-full max-w-md px-4">
        <div className="bg-gray-800 rounded-lg p-6 border-2 border-blue-500">
          <div className="text-center mb-4">
            <div className="text-sm text-blue-400 font-medium mb-2">
              Exercise {currentExerciseInRound + 1} of {exercises.length}
            </div>
            <div className="text-3xl font-bold text-white mb-2">
              {currentExercise.name}
            </div>
            <div className="text-2xl text-gray-300">
              {currentExercise.reps} reps
            </div>
            {currentExercise.instructions && (
              <div className="text-sm text-gray-400 mt-3">
                {currentExercise.instructions}
              </div>
            )}
          </div>
          
          {/* Complete Exercise Button */}
          <button
            onClick={handleExerciseComplete}
            className={`w-full py-4 rounded-lg font-bold text-lg transition-all ${
              remainingTime > 0
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'bg-gray-600 cursor-not-allowed text-gray-400'
            }`}
            disabled={remainingTime === 0}
          >
            {currentExerciseInRound < exercises.length - 1 
              ? '✓ Next Exercise' 
              : '🎉 Complete Round'
            }
          </button>
        </div>
      </div>
      
      {/* Exercise Checklist - Visual Progress */}
      <div className="flex justify-center gap-3 px-4">
        {exercises.map((ex, idx) => (
          <div 
            key={idx}
            className={`flex flex-col items-center`}
          >
            <div className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all ${
              idx < currentExerciseInRound 
                ? 'bg-green-500 border-green-500' 
                : idx === currentExerciseInRound 
                ? 'bg-blue-500 border-blue-500 animate-pulse scale-110' 
                : 'bg-gray-700 border-gray-600'
            }`}>
              <span className="text-sm text-white font-bold">
                {idx < currentExerciseInRound ? '✓' : idx + 1}
              </span>
            </div>
            <div className="text-xs text-gray-400 mt-1 text-center max-w-[60px] truncate">
              {ex.name}
            </div>
          </div>
        ))}
      </div>
      
      {/* Round History - Last 3 rounds */}
      {roundsCompleted > 0 && (
        <div className="w-full max-w-md px-4">
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-xs text-gray-400 font-medium mb-2">ROUND HISTORY</div>
            <div className="space-y-1">
              {Array.from({length: Math.min(3, roundsCompleted)}, (_, i) => {
                const roundNum = roundsCompleted - i;
                return (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-gray-300">Round {roundNum}</span>
                    <span className="text-green-400">✓ Complete</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      
      {/* Time's Up Message */}
      {remainingTime === 0 && (
        <div className="text-center p-4 bg-green-900/30 border border-green-600 rounded-lg">
          <div className="text-3xl font-bold text-green-400 mb-2">⏰ Time's Up!</div>
          <div className="text-lg text-white mb-1">
            {roundsCompleted} Round{roundsCompleted !== 1 ? 's' : ''} Completed
          </div>
          <div className="text-sm text-gray-300">
            {currentExerciseInRound > 0 && `+ ${currentExerciseInRound} partial exercise${currentExerciseInRound > 1 ? 's' : ''}`}
          </div>
        </div>
      )}
    </div>
  );
};

